/**
 * Internet Status Utility
 * Provides functions to check and monitor internet connectivity
 */

class InternetStatusChecker {
  constructor() {
    this.isOnline = navigator.onLine;
    this.listeners = [];
    this.checkInterval = null;
    this.checkUrl = 'https://www.google.com/favicon.ico'; // Lightweight URL to check connectivity
  }

  /**
   * Check if device is currently online
   * @returns {boolean} True if online, false if offline
   */
  isCurrentlyOnline() {
    return navigator.onLine;
  }

  /**
   * Check internet connectivity by making a fetch request
   * @param {string} url - URL to check against (default: Google favicon)
   * @param {number} timeout - Timeout in milliseconds (default: 5000)
   * @returns {Promise<boolean>} Promise that resolves to true if online, false if offline
   */
  async checkConnectivity(url = this.checkUrl, timeout = 1200) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(url, {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal,
        cache: 'no-cache',
      });

      clearTimeout(timeoutId);
      return true;
    } catch (error) {
      console.warn('Internet connectivity check failed:', error.message);
      return false;
    }
  }

  /**
   * Start monitoring internet status changes
   * @param {Function} callback - Callback function to execute on status change
   * @param {number} checkInterval - Interval in milliseconds for periodic checks (default: 30000)
   */
  startMonitoring(callback, checkInterval = 30000) {
    // Add event listeners for online/offline events
    const handleOnline = () => {
      this.isOnline = true;
      callback(true, 'online');
    };

    const handleOffline = () => {
      this.isOnline = false;
      callback(false, 'offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Store listeners for cleanup
    this.listeners.push({ type: 'online', handler: handleOnline }, { type: 'offline', handler: handleOffline });

    // Start periodic connectivity checks
    this.startPeriodicChecks(callback, checkInterval);

    // Initial check
    this.checkConnectivity().then((isOnline) => {
      if (this.isOnline !== isOnline) {
        this.isOnline = isOnline;
        callback(isOnline, isOnline ? 'online' : 'offline');
      }
    });
  }

  /**
   * Start periodic connectivity checks
   * @param {Function} callback - Callback function to execute on status change
   * @param {number} interval - Check interval in milliseconds
   */
  startPeriodicChecks(callback, interval) {
    this.stopPeriodicChecks();

    this.checkInterval = setInterval(async () => {
      const isOnline = await this.checkConnectivity();
      if (this.isOnline !== isOnline) {
        this.isOnline = isOnline;
        callback(isOnline, isOnline ? 'online' : 'offline');
      }
    }, interval);
  }

  /**
   * Stop periodic connectivity checks
   */
  stopPeriodicChecks() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Stop monitoring internet status
   */
  stopMonitoring() {
    // Remove event listeners
    this.listeners.forEach(({ type, handler }) => {
      window.removeEventListener(type, handler);
    });
    this.listeners = [];

    // Stop periodic checks
    this.stopPeriodicChecks();
  }

  /**
   * Get current connection information
   * @returns {Object} Connection information object
   */
  getConnectionInfo() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    return {
      isOnline: this.isOnline,
      effectiveType: connection?.effectiveType || 'unknown',
      downlink: connection?.downlink || 'unknown',
      rtt: connection?.rtt || 'unknown',
      saveData: connection?.saveData || false,
    };
  }
}

// Create singleton instance
const internetStatusChecker = new InternetStatusChecker();

// Export utility functions
export const checkInternetStatus = {
  /**
   * Quick check if device is online
   */
  isOnline: () => internetStatusChecker.isCurrentlyOnline(),

  /**
   * Check connectivity with fetch request
   */
  checkConnectivity: (url, timeout) => internetStatusChecker.checkConnectivity(url, timeout),

  /**
   * Start monitoring internet status
   */
  startMonitoring: (callback, interval) => internetStatusChecker.startMonitoring(callback, interval),

  /**
   * Stop monitoring internet status
   */
  stopMonitoring: () => internetStatusChecker.stopMonitoring(),

  /**
   * Get connection information
   */
  getConnectionInfo: () => internetStatusChecker.getConnectionInfo(),
};

// Export the class for advanced usage
export { InternetStatusChecker };

export default checkInternetStatus;
